import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { adminAPI } from '../services/api'
import type { EngineEvent } from '../types/adminDiagnostics'

/**
 * Recent engine events on /admin/diagnostics: deterministic fact alerts, no AI.
 *
 * The same events the notification bell shows superusers. Read state is per
 * admin on the server; dismissing one here marks it read for this admin only.
 * `?event=<id>` (the bell's link) highlights that event, shows its facts and
 * marks it opened.
 */

const LIMIT = 10

const SEVERITY_STYLES: Record<EngineEvent['severity'], string> = {
  critical: 'border-danger-300 bg-danger-50 text-danger-800',
  warning: 'border-amber-300 bg-amber-50 text-amber-900',
  info: 'border-gray-200 bg-gray-50 text-gray-800',
}

const SEVERITY_LABELS: Record<EngineEvent['severity'], string> = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
}

const factValue = (value: unknown) =>
  value === null || value === undefined ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)

const EngineEventsStrip = () => {
  const [events, setEvents] = useState<EngineEvent[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [searchParams] = useSearchParams()
  const focusId = Number(searchParams.get('event')) || null

  const load = useCallback(async () => {
    try {
      const { events: loaded } = await adminAPI.engineEvents({ limit: LIMIT })
      setEvents(loaded)
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Arriving from the bell: show that event's facts and count it as opened.
  useEffect(() => {
    if (!focusId || !events) return
    setExpanded(focusId)
    const event = events.find((e) => e.id === focusId)
    if (event && !event.read) {
      adminAPI
        .markEngineEventSeen(focusId, 'opened')
        .then(() => setEvents((prev) => prev && prev.map((e) => (e.id === focusId ? { ...e, read: true } : e))))
        .catch(() => undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, events === null])

  const dismiss = async (id: number) => {
    try {
      await adminAPI.markEngineEventSeen(id, 'dismissed')
      setEvents((prev) => prev && prev.map((e) => (e.id === id ? { ...e, read: true } : e)))
    } catch {
      // Leave it unread; the next load will show the truth.
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white" aria-label="Recent engine events">
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent engine events</h2>
        <p className="text-sm text-gray-500">
          Fact alerts from fixed rules over the engine&apos;s own state: no AI. The same alerts appear in the
          notification bell for admins.
        </p>
      </div>
      {failed ? (
        <p className="p-4 text-sm text-gray-500" data-testid="events-failed">
          Engine events could not be loaded.
        </p>
      ) : events === null ? (
        <p className="p-4 text-sm text-gray-500">Loading…</p>
      ) : events.length === 0 ? (
        <p className="p-4 text-sm text-gray-500" data-testid="events-empty">
          Nothing notable recorded.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {events.map((e) => {
            const open = expanded === e.id
            return (
              <li
                key={e.id}
                data-testid={`engine-event-${e.id}`}
                className={`p-3 ${e.id === focusId ? 'ring-2 ring-inset ring-accent' : ''}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    <span
                      className={`shrink-0 rounded border px-1.5 py-0.5 text-xs font-semibold ${SEVERITY_STYLES[e.severity]}`}
                    >
                      {SEVERITY_LABELS[e.severity] ?? e.severity}
                    </span>
                    <p className={`text-sm ${e.read ? 'text-gray-600' : 'font-medium text-gray-900'}`}>{e.summary}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                    <span title={new Date(e.fired_at).toLocaleString()}>
                      {formatDistanceToNow(new Date(e.fired_at), { addSuffix: true })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setExpanded(open ? null : e.id)}
                      aria-expanded={open}
                      className="text-accent hover:underline"
                    >
                      {open ? 'Hide facts' : 'Facts'}
                    </button>
                    {e.read ? (
                      <span data-testid={`event-read-${e.id}`}>Read</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => dismiss(e.id)}
                        className="text-gray-600 hover:underline"
                        data-testid={`event-dismiss-${e.id}`}
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
                {open && (
                  <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 rounded bg-gray-50 p-2 font-mono text-xs text-gray-700 sm:grid-cols-2">
                    {Object.keys(e.facts).length === 0 ? (
                      <dd>No facts recorded.</dd>
                    ) : (
                      Object.entries(e.facts).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <dt className="text-gray-500">{k}:</dt>
                          <dd>{factValue(v)}</dd>
                        </div>
                      ))
                    )}
                  </dl>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

export default EngineEventsStrip
